import { defineComponent } from 'vue';
import { Form, FormItem, Input, Button } from 'ant-design-vue';
import { CheckCircleOutlined, PhoneOutlined } from '@ant-design/icons-vue';
import { useStoreUser } from '@src/store/modules/user';
import * as api from '@src/api/auth';
import type { PhoneRegisterData } from '@src/api/auth';
import { useSendCode } from '../hooks/useSendCode';
import { useStoreLoginModal } from '@src/store/modules/loginModal';

export default defineComponent({
    name: 'PhoneBind',
    emits: ['setKey'],
    setup(props, context) {
        const storeUser = useStoreUser();
        const storeLoginModal = useStoreLoginModal();

        /**
         * data
         */
        const { sendCode, disabled: sendDisabled, loading: sendLoading, text: sendText, uuid: sendUuid } = useSendCode();

        const formLoading = ref(false);
        const formState = reactive({
            phonenumber: '',
            code: '',
        }) as Omit<PhoneRegisterData, 'uuid' | 'token'>;
        const formRef = ref();

        /**
         * methods
         */
        const phoneValidator = async (_rule: any, value: string) => {
            if (!value) {
                return Promise.reject('请输入手机号');
            } else if (!/^[1][3-9][0-9]{9}$/.test(value)) {
                return Promise.reject('请输入正确手机号');
            } else {
                return Promise.resolve();
            }
        };

        const handleSend = async () => {
            await formRef.value.validate(['phonenumber']);
            sendCode({ phone: formState.phonenumber });
        };
        const handleSubmit = async () => {
            await formRef.value.validate();
            formLoading.value = true;
            const res = await api
                .phoneValidate({
                    uuid: sendUuid.value,
                    token: storeUser.auth.token, // 微信来源 Token
                    ...formState,
                })
                .finally(() => {
                    formLoading.value = false;
                });
            await storeLoginModal.handleLoginSuccess((res as any).token, '绑定成功');
        };

        return () => (
            <div class='phone-bind'>
                <div class='phone-bind-header'>
                    <p class='g-flex g-items-center'>
                        <CheckCircleOutlined />
                        扫码成功
                    </p>
                    <span>为了您的账户安全，防止账号丢失及方便找回， 请绑定手机号</span>
                </div>
                <Form model={formState} ref={formRef}>
                    <FormItem name='phonenumber' rules={[{ required: true, validator: phoneValidator, trigger: 'blur' }]}>
                        <Input
                            v-model:value={formState.phonenumber}
                            placeholder='请输入手机号'
                            autocomplete='off'
                            v-slots={{ prefix: () => <PhoneOutlined /> }}
                        />
                    </FormItem>
                    <div class='phone-code g-flex'>
                        <FormItem class='g-flex-1' name='code' rules={[{ required: true, message: '请输入验证码', trigger: 'blur' }]}>
                            <Input
                                v-model:value={formState.code}
                                onPressEnter={handleSubmit}
                                placeholder='请输入验证码'
                                autocomplete='off'
                            />
                        </FormItem>
                        <Button
                            type='primary'
                            class='g-ml-[14px] g-h-[44px] g-w-[105px] g-flex-shrink-0 g-rounded-[10px] g-font-size-[14px]'
                            loading={sendLoading.value}
                            disabled={sendDisabled.value}
                            onClick={handleSend}
                        >
                            {sendText.value}
                        </Button>
                    </div>
                    <Button
                        type='primary'
                        class='g-mt-[12px] g-h-[44px] g-w-full g-rounded-[22px] g-p-[0] g-font-size-[15px]'
                        onClick={handleSubmit}
                        loading={formLoading.value}
                    >
                        立即绑定
                    </Button>
                    <div
                        class='g-mt-[18px] g-text-align-center g-font-size-[14px] g-color-[#8693AB]'
                        onClick={() => {
                            context.emit('setKey', '手机号注册');
                        }}
                    >
                        <span>还没账号？点击 </span>
                        <span class='g-cursor-pointer g-color-[var(--color-master)]'>手机号注册</span>
                    </div>
                </Form>
            </div>
        );
    },
});
